import React, { useState, useContext, useEffect } from "react";
import { AppPage, NoIconAlert } from "../../components";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import { Alert, Button, Choice } from "@cmsgov/ds-medicare-gov";
import Routes from "../../app/routes";
import { PharmacyType, ActionType, PDE } from "../../@types";
import { AppStore } from "../../app/store";
import { RouteComponentProps, withRouter } from "react-router";

interface NextPageProps {
  drugCosts: string;
  pdes: PDE[] | undefined;
  search: string;
}

// Pulled this out into a standalone function in order to make testing easier
export const determineNextPage = ({
  drugCosts,
  pdes,
  search,
}: NextPageProps): string => {
  let uri;

  if (drugCosts === "include") {
    if (pdes && pdes.length) {
      uri = `${Routes.pdePage}${search}`;
    } else {
      uri = `${Routes.managePrescriptions}${search}`;
    }
  } else {
    uri = `${Routes.searchResults}${search}`;
  }

  return uri;
};

const DrugSearchPreferencesPage: React.FunctionComponent<RouteComponentProps> = props => {
  const [drugCosts, setDrugCosts] = useState("");
  const [pharmacyType, setPharmacyType] = useState<PharmacyType>(
    PharmacyType.NONE
  );
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);

  const showContinueButton =
    drugCosts === "exclude" ||
    (drugCosts === "include" && pharmacyType !== PharmacyType.NONE);

  /* istanbul ignore next: tested elsewhere */
  const selectPharmacyType = (type: PharmacyType): void => {
    setPharmacyType(type);

    dispatch({ type: ActionType.UPDATE_PHARMACY_TYPE, payload: type });
  };

  useEffect(() => {
    dispatch({ type: ActionType.RESET_PHARMACIES });
  }, []);

  /* istanbul ignore next: tested elsewhere */
  const goToNextPage = (): void => {
    const uri = determineNextPage({
      drugCosts,
      pdes: state.beneficiary ? state.beneficiary.pdes : [],
      search: props.location.search,
    });

    props.history.push(uri);
  };

  return (
    <AppPage heading={t("plan_search_prefs.header")}>
      <div className="DrugSearchPreferencePage ds-u-margin-bottom--3">
        <span className="sr-only">
          {t("sr_only_additional_questions_may_appear")}
        </span>
        <fieldset className="ds-c-fieldset ds-u-margin--0">
          <legend className="ds-u-margin--0 mct-u-max-width--none ds-h3 ds-u-margin-bottom--1 ds-u-padding--0">
            {t("plan_search_prefs.include_drug_costs")}
          </legend>

          <Choice
            type="radio"
            name="drugCosts"
            value="include"
            className="e2e-drug-search-pref--yes"
            checked={drugCosts === "include"}
            onChange={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - See Drug Costs", "Yes");
              setDrugCosts("include");
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.great"),
              t("plan_search_prefs.to_see_drug_costs")
            )}
          >
            {t("yes")}
          </Choice>

          <Choice
            type="radio"
            name="drugCosts"
            value="exclude"
            className="e2e-drug-search-pref--no"
            checked={drugCosts === "exclude"}
            onChange={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - See Drug Costs", "No");
              /* istanbul ignore next: tested elsewhere */
              selectPharmacyType(PharmacyType.NONE);
              setDrugCosts("exclude");
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.are_you_sure"),
              t("plan_search_prefs.need_to_enter_drugs")
            )}
          >
            {t("no")}
          </Choice>
        </fieldset>

        {drugCosts === "include" && (
          <fieldset className="ds-c-fieldset ds-u-margin-x--0 ds-u-margin-top--3">
            <legend className="ds-u-margin--0 mct-u-max-width--none ds-h3 ds-u-margin-bottom--1 ds-u-padding--0">
              <span className="ds-h3 ds-u-text-transform--none">
                {t("plan_search_prefs.prescription_fill")}
              </span>
            </legend>

            <Choice
              type="radio"
              name="pharmacy"
              value={PharmacyType.RETAIL}
              className="e2e-pharmacy-pref--retail"
              checked={pharmacyType === PharmacyType.RETAIL}
              onChange={
                /* istanbul ignore next: tested elsewhere */
                (): void => {
                  sendTealiumEvent("Find Plans - Drug Fill Type", "Retail");
                  /* istanbul ignore next: tested elsewhere */
                  selectPharmacyType(PharmacyType.RETAIL);
                }
              }
              checkedChildren={
                <Alert role="alert" hideIcon={true}>
                  {t("plan_search_prefs.most_used_pharmacies")}
                </Alert>
              }
            >
              {t("plan_search_prefs.retail_pharmacy")}
            </Choice>

            <Choice
              type="radio"
              name="pharmacy"
              checked={pharmacyType === PharmacyType.MAIL}
              value={PharmacyType.MAIL}
              className="e2e-pharmacy-pref--mail"
              onChange={
                /* istanbul ignore next: tested elsewhere */
                (): void => {
                  sendTealiumEvent("Find Plans - Drug Fill Type", "Mail Order");
                  selectPharmacyType(PharmacyType.MAIL);
                }
              }
            >
              {t("plan_search_prefs.mail_order_pharmacy")}
            </Choice>

            <Choice
              type="radio"
              name="pharmacy"
              value={PharmacyType.MAIL_AND_RETAIL}
              className="e2e-pharmacy-pref--both"
              checked={pharmacyType === PharmacyType.MAIL_AND_RETAIL}
              onChange={
                /* istanbul ignore next: tested elsewhere */
                (): void => {
                  sendTealiumEvent("Find Plans - Drug Fill Type", "Both");
                  selectPharmacyType(PharmacyType.MAIL_AND_RETAIL);
                }
              }
              checkedChildren={NoIconAlert(
                "",
                t("plan_search_prefs.most_used_pharmacies")
              )}
            >
              {t("both")}
            </Choice>
          </fieldset>
        )}

        {showContinueButton && (
          <section className="ContinueSection">
            <Button
              className="e2e-drug-search-pref--continue"
              variation="primary"
              size="big"
              onClick={goToNextPage}
            >
              {t("continue")}
            </Button>
          </section>
        )}
      </div>
    </AppPage>
  );
};

export default withRouter(DrugSearchPreferencesPage);
